import {
  CheckOutlined,
  QuestionCircleOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { Button, ButtonProps } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';
import { SalesForceIcon } from '@mm/icons';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useSalesForceCRUDActions } from '@mm/state/actions/SalesForceCRUDActions';
import ConnectionModel, {
  ConnectionKind,
} from '@mm/state/model/ConnectionModel';
import { selectCrmsLeadsSent } from '@mm/state/selectors/connectionsSelectors';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { OAuthPopupActionButton } from '@mm/stateful-components/OAuth/OAuthPopupActionButton';
import { Spin } from 'antd';
import React, { ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ButtonColor } from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { BaseIntegration } from '../../../BaseIntegration/BaseIntegration';
import { BaseIntegrationEmptyState } from '../../../BaseIntegration/BaseIntegrationEmptyState';
import { BaseIntegrationHeader } from '../../../BaseIntegration/BaseIntegrationHeader';
import { Help } from '../KlaviyoIntegrations/KlaviyoHelpLink';
import { SalesForceSettings } from './SalesForceSettings';
import { useConnectSalesForce } from './use-connect-salesforce';
import styles from './SalesForceIntegrations.module.css';
import classNames from 'classnames';
import { HelpCenter } from '../../../../../../../../common/components/HelpCenter/HelpCenter';

export const SalesForceIntegrations = ({
  version,
  connection: activeConnection,
}: {
  version?: 'widget';
  connection?: ConnectionModel;
}) => {
  const hasNativeIntegrationsFeature = useSelector(
    combinedFeatureFlagsSelector,
  ).nativeIntegrations;
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const oAuthButtonName = 'Add a SalesForce Account';
  const { invalidateStore } = useSalesForceCRUDActions();
  const { create } = useConnectionCRUDActions();
  const {
    accounts,
    retrieveSalesForceAccounts,
    fetchOAuthUrl,
    onTokenConnected,
    salesForceConnections,
    getSaleForceConnections,
  } = useConnectSalesForce();
  const [busy, setBusy] = useState(false);
  const [accountsFetched, setAccountsFetched] = useState(false);
  const connection =
    activeConnection ||
    salesForceConnections.filter(c => c.kind === ConnectionKind.AUDIENCE)?.[0];

  const connectedToken = accounts.find(
    token => token.id === connection?.salesforceAccessToken?.id,
  );
  const showSettings = !!connection && !!connectedToken;
  const connectDisabled = connectedToken?.readOnly;

  const isWidgetMode = version === 'widget';

  const sectionProps = {
    landmineName: landmineNames.SALESFORCE_UPGRADE,
    featureFlag: 'nativeIntegrations',
  };
  const otherProps = {
    ...sectionProps,
    onRefreshNeeded: onTokenConnected,
    connectParams: [connectedToken?.id],
  };

  // loads webhooks once oauth tokens/api keys are loaded
  useEffect(() => {
    (async () => {
      if (accountsFetched) {
        setBusy(true);
        await getSaleForceConnections();
        setBusy(false);
      }
    })();
  }, [accountsFetched]);

  useEffect(() => {
    (async () => {
      setBusy(true);
      await retrieveSalesForceAccounts();
      setAccountsFetched(true);
      setBusy(false);
    })();
    return () => {
      invalidateStore();
    };
  }, []);

  const crmsLeadsSent = useSelector(selectCrmsLeadsSent);
  const leadsSent = crmsLeadsSent?.salesforceContactsSent;

  const { companyName = 'Customers.ai' } = useWhitelabelConfig();

  const oAuthButton = (
    buttonName?: string | ReactNode,
    buttonProps?: Pick<ButtonProps, 'size' | 'color' | 'css' | 'disabled'>,
  ) => {
    const defaultButtonProps = buttonProps || {
      css: {
        container: classNames(
          'integrations__header-add-button-secondary',
          styles.button,
        ),
      },
    };

    return (
      <OAuthPopupActionButton
        fetchOauthPopupUrl={fetchOAuthUrl}
        oauthSuccessMessageType="salesForceTokenReceived"
        buttonProps={defaultButtonProps}
        popupName="integrationConnect"
        spinnerProps={{ width: '30px' }}
        popupWidth={600}
        popupHeight={600}
        {...otherProps}
      >
        {buttonName || 'Add an Account'}
      </OAuthPopupActionButton>
    );
  };

  const onAddConnectionClick = () => {
    if (!hasNativeIntegrationsFeature) return;
    if (accounts.length && !connection) {
      create(facebookPageId, {
        name: 'SalesForce Connection',
        connectionKind: 'salesforce',
        salesforceParams: { accessTokenId: accounts[0].id },
      });
    }
  };

  const addConnectionButton = (
    <Button
      css={{ container: 'integrations__empty-state-button' }}
      onClick={onAddConnectionClick}
    >
      Add a Connection
    </Button>
  );

  const emptyStateButton =
    accounts.length && !connection
      ? addConnectionButton
      : hasNativeIntegrationsFeature
      ? oAuthButton(oAuthButtonName, {
          css: { container: 'integrations__empty-state-button' },
        })
      : oAuthButtonName;

  const header = () => (
    <div className="integrations__header-actions">
      <HelpCenter
        helpLink="https://customers.ai/help/l/en/article/oqqh8uzfjl-sales-force"
        title=""
        icon={<QuestionCircleOutlined style={{ fontSize: 20 }} />}
      />
      {!!leadsSent && (
        <div className="integrations__header-status">
          <TeamOutlined className="integrations__header-status-icon" />
          <div className="integrations__header-status-count">{leadsSent}</div>
          Leads sent
        </div>
      )}
      {connectDisabled ? (
        <Button
          css={{ container: 'integrations__header-add-button-secondary' }}
          color={ButtonColor.secondaryBlue}
          disabled={connectDisabled}
        >
          <CheckOutlined /> Connected Directly
        </Button>
      ) : (
        oAuthButton(oAuthButtonName)
      )}
    </div>
  );

  if (isWidgetMode && accountsFetched) {
    return busy ? (
      <Spin tip="Loading..." />
    ) : showSettings ? (
      <SalesForceSettings
        account={connectedToken}
        oAuthButton={oAuthButton}
        version="widget"
        css={{ content: styles.salesforceSettings }}
      />
    ) : (
      <BaseIntegrationEmptyState
        button={emptyStateButton}
        icon={<SalesForceIcon />}
        {...sectionProps}
      />
    );
  }

  return (
    <BaseIntegration
      openOnMount
      titleComponent={
        <BaseIntegrationHeader
          title="SalesForce"
          upgradeButtonName={
            !connectDisabled ? 'Add an Account' : 'Add a Connection'
          }
          subtitle={`Automatically sync your ${companyName} contacts to SalesForce`}
          icon={<SalesForceIcon />}
          headerRightComponent={header()}
          css={{ icon: 'integrations__salesforce-header-icon ' }}
          {...sectionProps}
        />
      }
      integrationType="salesforce"
      emptyStateConfig={{
        button: emptyStateButton,
        icon: <SalesForceIcon size={60} />,
        ...sectionProps,
      }}
      showEmptyState={!showSettings}
    >
      {showSettings && (
        <SalesForceSettings
          account={connectedToken}
          oAuthButton={oAuthButton}
        />
      )}
    </BaseIntegration>
  );
};
